/**
 * Turning a wire failure into a sentence a person reads. Once.
 *
 * This was written four times, with four different orders, and the four orders
 * gave four different answers to the SAME failure. Measured on 2026-08-27
 * against `404 / code bad_request / messageKey error.http` - what a bare
 * framework `HTTPException` serialised as on the day (the worker names the
 * code its status means now, `httpStatusEnvelope` in `apps/api-worker`, so a
 * bare 404 says `not_found`; the four answers below are what that one wire
 * failure produced):
 *
 *   partner   "That record does not exist, or is not yours."
 *   internal  "Something went wrong."
 *   bakery    "Check the values and try again."   <- the WEB-014 defect, again
 *   guest     "That request could not be completed."
 *
 * The bakery line is the one that matters: telling a counter to check its input
 * when nothing was wrong with its input is the exact bug the partner dashboard
 * fixed in 2026 and the fix reached ONE of the four copies. So the order lives
 * here now, and each surface supplies only its own words.
 *
 * The order, and why each step is where it is:
 *
 *  1. The KEY the server chose, unless it is one of the three that say nothing
 *     the status has not already said (`isStatusFirstErrorKey`). The wire
 *     vocabulary is closed and every registered key is more specific than a
 *     status by construction, so it wins - that is the whole reason NFR-006
 *     put a key on the wire.
 *  2. A request that never reached a server (`network_error`) has no status to
 *     read: 0 is not a refusal.
 *  3. The HTTP STATUS, which outranks the body's `code` whenever the two
 *     disagree. Trusting the code is what told an owner opening a deleted
 *     apartment to correct the highlighted fields, with no form and nothing
 *     highlighted: a bare framework 404 was serialised `code: 'bad_request'`.
 *     The worker's table has since been widened to the statuses it knows
 *     (`HTTP_STATUS_CODE`), so that particular 404 now says `not_found` - but
 *     the rule stands and is not a leftover of the defect. Every status the
 *     table does NOT name still falls to `bad_request`, so a bare 402, 405 or
 *     451 reaches here claiming the caller's input was wrong; and this
 *     renderer answers failures from anything that can produce a
 *     `WireFailure`, not from one server's current table.
 *  4. The CODE, through a table that is exhaustive over `ApiFailureCode` by
 *     type. A new code is a typecheck failure here rather than a silent
 *     fall-through to "Something went wrong." in four places at once.
 *
 * At every step the SURFACE's own catalogue is consulted before the platform's,
 * so a dashboard that has better words for `error.notFound` than "We could not
 * find that." keeps them, and a surface that has never heard of a key still
 * says the platform's sentence instead of a generic one.
 *
 * Framework-free and DOM-free on purpose: the counter tablet calls this from a
 * plain module, and this package compiles without the DOM lib. That is also why
 * the failure is recognised structurally rather than with `instanceof
 * ApiRequestError` - `@croissants/contracts/client` is not reachable from here.
 */

import type { ApiFailureCode } from '@croissants/contracts/common';
import { isStatusFirstErrorKey } from '@croissants/contracts/messages';
import { catalogues, en } from './locales/index';
import type { Locale, MessageKey } from './types';

/**
 * What a renderer knows about a failure: the three fields `ApiRequestError`
 * carries that decide which sentence is right.
 */
export interface WireFailure {
  readonly status: number;
  readonly code: ApiFailureCode;
  readonly messageKey: string;
}

/**
 * A surface's own copy for the current locale, as a plain record. Every app
 * builds its catalogue with `createCatalog()`, so this is
 * `<app>Catalog.catalogues[locale]` and nothing has to be re-typed.
 */
export type AppCatalogue = Readonly<Record<string, unknown>>;

/** The sentence for a failure nothing else could describe. */
const GENERIC: MessageKey = 'error.unexpected';

/**
 * The sentence the HTTP status alone already justifies, or null when the status
 * is not specific enough to say anything on its own (402, 409, 429, ...) and
 * the code has to answer instead.
 */
export function messageKeyForStatus(status: number): MessageKey | null {
  if (status === 401) return 'error.unauthorized';
  if (status === 403) return 'error.forbidden';
  // 410 is a 404 the server can prove: the record existed and is gone.
  if (status === 404 || status === 410) return 'error.notFound';
  if (status === 400 || status === 422) return 'error.validation';
  if (status >= 500) return GENERIC;
  return null;
}

/**
 * Every code the platform or the client can produce, and the sentence it means.
 *
 * Exhaustive by TYPE, which is the point: the four copies this replaces each
 * ended their switch with a `default` that said "Something went wrong.", so a
 * code added to the contract reached a screen as a shrug in four places and
 * nothing failed. Here it does not compile.
 *
 * A code with no sentence anywhere in the product on 2026-08-27 is mapped to
 * the generic one deliberately rather than given words nobody asked for; the
 * pairing family is answered by `pin-refusal.ts` on the tablet, which reads the
 * code itself and never comes through here.
 */
export const MESSAGE_KEY_FOR_CODE: Readonly<Record<ApiFailureCode, MessageKey>> = {
  support_case_stale: 'error.support_case_stale',
  refund_over_charged: 'error.refund_over_charged',
  support_case_invalid: 'error.support_case_invalid',
  refund_unauthorized: 'error.refund_unauthorized',
  refund_order_not_final: 'error.refund_order_not_final',
  support_case_not_found: 'error.support_case_not_found',

  // The client's own two: no answer at all, and an answer of the wrong shape.
  network_error: 'error.network',
  stay_morning_unavailable: 'error.stay_morning_unavailable',
  invalid_response: GENERIC,
  // The HTTP-shaped refusals.
  bad_request: 'error.validation',
  unprocessable: 'error.validation',
  unauthorized: 'error.unauthorized',
  forbidden: 'error.forbidden',
  not_found: 'error.notFound',
  gone: 'error.notFound',
  conflict: 'error.conflict',
  idempotency_conflict: 'error.conflict',
  rate_limited: GENERIC,
  too_many_requests: GENERIC,
  internal: GENERIC,
  // The ordering journey.
  service_paused: 'error.servicePaused',
  not_servable: 'error.notServable',
  window_unavailable: 'error.windowUnavailable',
  window_passed: 'error.window_passed',
  ordering_closed: 'error.ordering_closed',
  capacity_exhausted: 'error.capacityExhausted',
  minimum_basket_not_met: 'error.minimumBasket',
  cancellation_closed: 'error.cancellationClosed',
  payment_failed: 'error.paymentFailed',
  // The delivery partner.
  courier_en_route: 'error.courier_en_route',
  // The order camera: the counter's own three refusals.
  bag_photo_required: 'error.bag_photo_required',
  bag_photo_not_ready: 'error.bag_photo_not_ready',
  bag_photo_limit: 'error.bag_photo_limit',
  provider_unavailable: 'error.provider_unavailable',
  invalid_provider: 'error.unknown_provider',
  provider_limit_violated: 'error.provider_limit',
  capability_unavailable: 'error.capability_unavailable',
  // Device pairing: answered by the counter's own refusal reader, not here.
  pin_rejected: GENERIC,
  pin_locked: GENERIC,
  pin_being_reset: GENERIC,
  pairing_requires_pin: GENERIC,
  pin_already_set: GENERIC,
  weak_pin: GENERIC,
  no_pin: GENERIC,
  no_contact: GENERIC,
  unknown_pairing_code: GENERIC,
  org_suspended: GENERIC,
  location_suspended: GENERIC,
};

/**
 * True when this is a failure the API produced, rather than a `TypeError` from
 * a render. Structural because `ApiRequestError` is unreachable from here (see
 * the module comment); the three fields together are shape enough that nothing
 * else in the product answers to it.
 */
export function isWireFailure(error: unknown): error is WireFailure {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as Partial<WireFailure>;
  return (
    typeof candidate.status === 'number' &&
    typeof candidate.code === 'string' &&
    typeof candidate.messageKey === 'string'
  );
}

/**
 * The surface's own words for a key, then the platform's.
 *
 * A parameterised string is refused at both levels: a key whose copy carries a
 * `{placeholder}` cannot be rendered from a failure that supplies no
 * parameters, and rendering it verbatim puts a brace on the screen.
 */
function sentenceFor(
  key: string,
  locale: Locale,
  app: AppCatalogue | undefined,
): string | undefined {
  const own = app?.[key];
  if (typeof own === 'string' && !own.includes('{')) return own;
  const platform = (catalogues[locale] as AppCatalogue)[key];
  if (typeof platform === 'string' && !platform.includes('{')) return platform;
  return undefined;
}

/**
 * What a person is told when a call fails, in their own language.
 *
 * `app` is the surface's own catalogue for `locale`; omit it and every sentence
 * comes from the platform's.
 */
export function errorSentence(error: unknown, locale: Locale, app?: AppCatalogue): string {
  const generic = sentenceFor(GENERIC, locale, app) ?? en['error.unexpected'];
  if (!isWireFailure(error)) return generic;

  if (!isStatusFirstErrorKey(error.messageKey)) {
    const named = sentenceFor(error.messageKey, locale, app);
    if (named !== undefined) return named;
  }
  if (error.code === 'network_error') {
    return sentenceFor('error.network', locale, app) ?? generic;
  }
  const byStatus = messageKeyForStatus(error.status);
  if (byStatus !== null) {
    const said = sentenceFor(byStatus, locale, app);
    if (said !== undefined) return said;
  }
  const byCode = MESSAGE_KEY_FOR_CODE[error.code];
  return (byCode === undefined ? undefined : sentenceFor(byCode, locale, app)) ?? generic;
}
